import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/i18n";

export default function EventDialog({ open, onOpenChange, onCreated, defaultDate }) {
  const { tf } = useLang();
  const [players, setPlayers] = useState([]);
  const [form, setForm] = useState({
    title: "",
    type: "call",
    starts_at: "",
    ends_at: "",
    player_id: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/players").then((r) => setPlayers(r.data)).catch(() => {});
      const d = defaultDate ? new Date(defaultDate) : new Date();
      d.setHours(10, 0, 0, 0);
      const iso = toLocal(d);
      const end = new Date(d.getTime() + 60 * 60 * 1000);
      setForm((f) => ({ ...f, starts_at: iso, ends_at: toLocal(end) }));
    }
  }, [open, defaultDate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.starts_at) { toast.error(tf("Titre et date requis")); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        player_id: form.player_id || null,
      };
      const r = await api.post("/events", payload);
      toast.success(tf("Événement créé"));
      onCreated?.(r.data);
      onOpenChange(false);
      setForm({ title: "", type: "call", starts_at: "", ends_at: "", player_id: "", notes: "" });
    } catch (err) {
      toast.error(tf("Erreur"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle>{tf("Nouvel événement")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>{tf("Titre")}</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Appel DS Le Mans" data-testid="event-title-input" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{tf("Type")}</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="event-type-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">{tf("Appel")}</SelectItem>
                  <SelectItem value="meeting">{tf("Rendez-vous")}</SelectItem>
                  <SelectItem value="follow-up">{tf("Relance")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{tf("Joueur")}</Label>
              <Select value={form.player_id} onValueChange={(v) => setForm({ ...form, player_id: v })}>
                <SelectTrigger data-testid="event-player-trigger"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {players.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{tf("Début")}</Label>
              <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="event-start-input" />
            </div>
            <div>
              <Label>{tf("Fin")}</Label>
              <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="event-end-input" />
            </div>
          </div>
          <div>
            <Label>{tf("Notes")}</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="event-notes-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
            <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="event-submit-btn">
              {saving ? tf("Création…") : tf("Créer")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function toLocal(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
